import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "sonner";
import AuthFlowLayout from "../../layouts/AuthFlowLayout";
import IconLink from "../../components/IconLink";
import Loader from "../../components/Loader";
import ButtonContainer from "../../components/ButtonContainer";
import GradientButton from "../../components/GradientButton";
import PasswordInput from "../../components/PasswordInput";
import TextInput from "../../components/TextInput";
import { useAuthService } from "../../api/authService";
import { User, UserRole } from "@quantizr-engineering/common/types/user";
import EmailInput from "../../components/EmailInput";

const InvitedRegistrationPage: React.FC = () => {
  const { registerInvitedUser, isRegisterLoading, findInvitedUser } =
    useAuthService();
  const navigate = useNavigate();
  const [invitedUser, setInvitedUser] = useState<User>();
  const { accessKey } = useParams();

  useEffect(() => {
    const fetchInvitedUser = async () => {
      try {
        if (!accessKey) {
          toast.error("Access key is missing");
          return;
        }
        const user = await findInvitedUser(accessKey);
        if (user) {
          setInvitedUser(user);
        } else {
          toast.error("Invited user not found");
        }
      } catch (error) {
        console.error("Error fetching invited user:", error);
        toast.error("Error fetching invited user");
      }
    };

    fetchInvitedUser();
  }, [accessKey]);

  const formik = useFormik({
    initialValues: {
      email: invitedUser?.email || "",
      firstName: invitedUser?.firstName || "",
      lastName: invitedUser?.lastName || "",
      password: "",
      termsOfServiceAccepted: false,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email format")
        .required("Email is required"),
      password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .required("Password is required")
      .matches(/[0-9]/, "Password must contain at least one number")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        "Password must contain at least one symbol"
      ),
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      termsOfServiceAccepted: Yup.boolean().oneOf(
        [true],
        "You must accept the terms of use"
      ),
    }),
    onSubmit: (values) => {
      if (!accessKey || !invitedUser || invitedUser.teamID === undefined) {
        if (!accessKey) {
          toast.error("Invalid access key");
        } else if (!invitedUser) {
          toast.error("Invited user not found");
        } else if (invitedUser.teamID === undefined) {
          toast.error("Team ID is missing.");
        }
        return;
      }

      const registrationData = {
        ...values,
        accessKey,
        team: {
          name: invitedUser.teamID,
        },
      };
      registerInvitedUser(registrationData)
        .then(() => {
          toast.success("Registration successful");
          navigate("/registration-success");
        })
        .catch(() => {
          toast.error("Registration failed. Please try again.");
        });
    },
  });

  return (
    <AuthFlowLayout
      headerText="Finish Your Registration"
      headerContent={
        <IconLink to="/" src="/Logo-light.svg" alt="Logo" className="mb-4" />
      }
      isLoading={isRegisterLoading}
      loader={<Loader />}
    >
      {invitedUser ? (
        <form onSubmit={formik.handleSubmit}>
          <TextInput
            {...formik.getFieldProps("firstName")}
            label="First Name"
            error={formik.touched.firstName && formik.errors.firstName}
          />
          <TextInput
            {...formik.getFieldProps("lastName")}
            label="Last Name"
            error={formik.touched.lastName && formik.errors.lastName}
          />
          <EmailInput
            {...formik.getFieldProps("email")}
            label="Email"
            error={formik.touched.email && formik.errors.email}
            disabled
          />
          <PasswordInput
            {...formik.getFieldProps("password")}
            label="Password"
            error={formik.touched.password && formik.errors.password}
          />
          <ButtonContainer
            firstButton={<GradientButton type="submit" text="Register" />}
          />
          <div className="flex items-center my-2">
            <input
              type="checkbox"
              {...formik.getFieldProps("termsOfServiceAccepted")}
              checked={formik.values.termsOfServiceAccepted}
              className="checkbox border-gray-300 [--chkbg:theme(colors.custom-dark-purple)] [--chkfg:white] checked:border-transparent mr-2"
            />
            <span>
              I agree to the{" "}
              <a
                className="text-blue-500"
                href="/terms-of-use"
                target="_blank"
              >
                Terms Of Use
              </a>
            </span>
          </div>
        </form>
      ) : (
        <p>Loading invited user details...</p>
      )}
    </AuthFlowLayout>
  );
};

export default InvitedRegistrationPage;
